import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, ShieldCheck, BadgeCheck, Clock, FileCheck } from "lucide-react";
import Seo from "@/components/Seo";
import ServiceGrid from "@/components/ServiceGrid";
import Faq from "@/components/Faq";
import { MaskLines, Reveal, Counter, SectionHead } from "@/components/Reveal";
import { CITIES, HERO_BG, PHONE, PHONE_HREF, EMAIL_HREF } from "@/data";
import { CY_HOME as T } from "@/data/cy";

const FEATURED = ["cardiff", "swansea", "newport", "wrexham", "bangor", "carmarthen", "llandudno", "aberystwyth"];
const BADGE_ICONS = [ShieldCheck, BadgeCheck, Clock, FileCheck];

const WelshHome = () => (
  <div data-testid="home-cy-page">
    <Seo title={T.seoTitle} description={T.seoDesc} lang="cy" path="/cy" altEn="/" />

    <section className="xp-noise relative flex min-h-[88vh] items-center overflow-hidden pt-16" data-testid="hero-section-cy">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="Trefi Cymru wedi'u diogelu gan Xtreme Protection" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-[#0A0C10]/60" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0A0C10] to-transparent" />
      </div>
      <div className="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-6">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="eyebrow flex items-center gap-3">
          <span className="h-px w-10 bg-[#F5A800]" />
          {T.eyebrow}
        </motion.p>
        <MaskLines className="font-condensed mt-6 text-5xl font-bold uppercase leading-[0.9] tracking-tight text-white sm:text-7xl lg:text-8xl" lines={T.h1} />
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }} className="mt-6 max-w-xl text-base leading-relaxed text-[#C8CCD2] sm:text-lg">
          {T.sub}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }} className="mt-8 flex flex-wrap gap-3">
          <Link to="/cy/lleoliadau" data-testid="hero-cy-locations-btn" className="group flex items-center gap-2 bg-[#F5A800] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-white">
            {T.ctaLocations}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to="/cy/gwasanaethau" data-testid="hero-cy-services-btn" className="flex items-center gap-2 border border-white/25 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:border-[#F5A800] hover:text-[#F5A800]">
            {T.ctaServices}
          </Link>
        </motion.div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
      <div className="grid grid-cols-2 gap-px border border-[#1C2027] bg-[#1C2027] lg:grid-cols-4" data-testid="stats-grid-cy">
        {T.stats.map((st, i) => (
          <Reveal key={st.l} delay={i * 0.08} className="h-full">
            <div className="flex h-full flex-col justify-between bg-[#10131A] p-6">
              <Counter to={st.v} suffix={st.s} className="font-condensed text-5xl font-bold text-[#F5A800]" />
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">{st.l}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <SectionHead index={T.standardIndex} eyebrow={T.standardEyebrow} title={T.standardTitle} testid="standard-heading-cy" />
        <Reveal delay={0.1}>
          <p className="mt-6 text-base leading-relaxed text-[#9BA1AB]">{T.standardP1}</p>
          <p className="mt-4 text-base leading-relaxed text-[#9BA1AB]">{T.standardP2}</p>
          <div className="mt-8 flex flex-wrap gap-2">
            {T.badges.map((b, i) => {
              const Icon = BADGE_ICONS[i];
              return (
                <span key={b} className="flex items-center gap-2 border border-[#F5A800]/30 bg-[#F5A800]/5 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#F5A800]">
                  <Icon className="h-3.5 w-3.5" />
                  {b}
                </span>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <SectionHead index={T.servicesIndex} eyebrow={T.servicesEyebrow} title={T.servicesTitle} testid="services-heading-cy" />
          <Reveal delay={0.1}>
            <Link to="/cy/gwasanaethau" data-testid="home-cy-all-services-btn" className="group flex items-center gap-2 border border-[#F5A800]/50 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-[#F5A800] transition-colors duration-300 hover:bg-[#F5A800] hover:text-[#0A0C10]">
              {T.allServices} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#9BA1AB]">{T.servicesSub}</p>
        <div className="mt-10">
          <ServiceGrid />
        </div>
      </div>
    </section>

    <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <SectionHead index={T.territoryIndex} eyebrow={T.territoryEyebrow} title={T.territoryTitle} testid="territory-heading-cy" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-[#9BA1AB]">{T.territorySub}</p>
              <Link to="/cy/lleoliadau" data-testid="home-cy-directory-btn" className="group mt-8 inline-flex items-center gap-2 border border-[#F5A800]/50 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-[#F5A800] transition-colors duration-300 hover:bg-[#F5A800] hover:text-[#0A0C10]">
                {T.fullDirectory} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="grid gap-px border border-[#1C2027] bg-[#1C2027] sm:grid-cols-2">
            {FEATURED.map((slug, i) => {
              const c = CITIES.find((x) => x.slug === slug);
              return (
                <Reveal key={slug} delay={(i % 2) * 0.08} y={14} className="h-full">
                  <Link to={`/locations/${c.slug}`} data-testid={`home-cy-city-${slug}`} className="group flex h-full items-center justify-between bg-[#10131A] p-5 transition-colors duration-300 hover:bg-[#161A22]">
                    <div>
                      <p className="font-condensed text-lg font-bold uppercase tracking-wide text-white transition-colors duration-300 group-hover:text-[#F5A800]">{c.name}</p>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">{c.county}</p>
                    </div>
                    <MapPin className="h-4 w-4 text-[#F5A800]/60 transition-transform duration-300 group-hover:scale-125" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <SectionHead index={T.faqIndex} eyebrow={T.faqEyebrow} title={T.faqTitle} testid="faq-heading-cy" />
        <div className="mt-10">
          <Faq items={T.faqs} idPrefix="home-cy-faq" />
        </div>
      </div>
    </section>

    <section className="border-t border-[#1C2027] bg-[#F5A800] py-14" data-testid="cta-band-cy">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 lg:flex-row lg:items-center">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#0A0C10]/60">{T.ctaEyebrow}</p>
          <h2 className="font-condensed mt-2 text-3xl font-bold uppercase leading-none text-[#0A0C10] sm:text-4xl">{T.ctaTitle}</h2>
        </div>
        <div className="flex flex-wrap gap-3">
          <a href={PHONE_HREF} data-testid="cta-cy-call-btn" className="bg-[#0A0C10] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#F5A800] transition-colors duration-300 hover:bg-[#1A1E26]">
            {T.ctaCall}
          </a>
          <a href={EMAIL_HREF} data-testid="cta-cy-email-btn" className="border-2 border-[#0A0C10] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-[#0A0C10] hover:text-[#F5A800]">
            {T.ctaEmail}
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default WelshHome;
